import {
  MetricBaseUnit,
  MetricRatioUnit,
  MetricUnit,
} from '@shared/models/units/metric-units.model';
import { UnitCategory, UnitSystem } from '@shared/models/units/unit-base.model';
import { mathJS } from '../mathjs';
import { CurrencyUnit } from '@shared/models/units/currency-units.model';
import { tryCatch } from '../utils.helpers';
import { CO2eUnit } from '@shared/models/units/co2e-units.model';
import {
  BP,
  BreakPoints,
  BreakPointsInstance,
  V,
} from '@shared/helpers/breakpoints/BreakPoints';
/**
 * Default conversion units for each unit category
 * When calling getDisplayValueData with unit: 'auto', the best fit unit will be selected from these units.
 * defaults can be defined for each unit category, and specifically for ratio units (as they may not have a category).
 */

type ListOrBreakPoints<Unit> = Unit[] | BreakPointsInstance<Unit, Unit>;

// All main Unit Categories should have defaults (not Partial)
type AutoConversionCategoryUnits = Record<
  UnitCategory,
  ListOrBreakPoints<MetricUnit | CurrencyUnit | CO2eUnit>
>;

// so we can add Ratio unit defaults. Partial, because we can add as needed
type AutoConversionBaseUnits = Partial<
  Record<MetricBaseUnit, ListOrBreakPoints<MetricUnit>>
>;
type AutoConversionRatioUnits = Partial<
  Record<MetricRatioUnit, ListOrBreakPoints<MetricUnit>>
>;

type AutoConversionUnitsRecord = AutoConversionCategoryUnits &
  AutoConversionRatioUnits &
  AutoConversionBaseUnits;
type LooseKeys<T> = { [key: string]: T[keyof T] }; // just a helper to convert the type to a looser type for easier index access

const metricDefaultConversionUnits: LooseKeys<AutoConversionUnitsRecord> = {
  // - auto-conversion breakpoints: "mm [2 m [1_000_000 km"
  [UnitCategory.Length]: BreakPoints(
    [V('mm'), BP('[2'), V('m'), BP('[1000000'), V('km')], 
    { mirror: true, unit: 'm' }
  ), // prettier-ignore

  // - auto-conversion breakpoints: "mm² [0.001 m²"
  [UnitCategory.Area]: BreakPoints(
    [V('mm^2'), BP('[0.001'), V('m^2')], 
    { mirror: true, unit: 'm^2' }
  ), // prettier-ignore

  [UnitCategory.Mass]: BreakPoints([V('kg')], { mirror: true, unit: 'kg' }),

  [UnitCategory.Frequency]: ['Hz', 'kHz', 'MHz', 'GHz'],
  [UnitCategory.Duration]: ['ns', 'ms', 's'],

  // - auto-conversion breakpoints: "L [0.01 m³"
  [UnitCategory.Volume]: BreakPoints(
    [V('l'), BP('[0.01'), V('m^3')], 
    { mirror: true, unit: 'm^3' }
  ), // prettier-ignore

  // - auto-conversion breakpoints (in seconds): "s [60 min [3600 hr"
  [UnitCategory.Time]: BreakPoints(
    [V('s'), BP('[60'), V('min'), BP('[3600'), V('h')],
    { mirror: true, unit: 's' }
  ), // prettier-ignore
  [UnitCategory.Temperature]: ['°C'],
  [UnitCategory.Amount]: [], // should not be used. Can be people, mol, pcs, unit, activity - there's no proper way to select the correct unit
  [UnitCategory.Light]: ['mcd', 'cd'],
  [UnitCategory.Angle]: ['rad'],
  [UnitCategory.Force]: ['N', 'kN'],
  [UnitCategory.Pressure]: ['Pa', 'kPa', 'MPa'],

  // - auto-conversion breakpoints: "W [1000 kW [1_000_000 GW"
  [UnitCategory.Power]: BreakPoints(  
    [V('W'), BP('[1000'), V('kW'), BP('[1000000'), V('GW')], 
    { mirror: true, unit: 'kW' }
  ), // prettier-ignore

  // - auto-conversion breakpoints: "kWh [1_000_000 GWh"
  [UnitCategory.Energy]: BreakPoints(
    [V('kWh'), BP('[1000000'), V('GWh')], 
    { mirror: true, unit: 'kWh' }
  ), // prettier-ignore

  [UnitCategory.ElectricCurrent]: ['mA', 'A'],
  [UnitCategory.ElectricPotential]: ['V', 'kV', 'MV', 'GV'],
  [UnitCategory.ElectricConductance]: ['S', 'kS', 'MS', 'GS'],
  [UnitCategory.ElectricInductance]: ['H', 'kH', 'MH', 'GH'],
  [UnitCategory.ElectricCharge]: ['C', 'kC', 'MC', 'GC'],
  [UnitCategory.ElectricResistance]: ['ohm'],

  // not sure if this will work as intended
  // - auto-conversion breakpoints: "L/s [0.01 m³/s"
  'l/s': BreakPoints(
    [V('l/s'), BP('[0.01'), V('m^3/s')], 
    { mirror: true, unit: 'm^3/s' }
  ), // prettier-ignore

  [UnitCategory.CurrencySEK]: ['SEK', 'kSEK', 'MSEK'],
  [UnitCategory.CurrencyUSD]: ['USD', 'kUSD', 'MUSD'],
  [UnitCategory.CurrencyEUR]: ['EUR', 'kEUR', 'MEUR'],
  [UnitCategory.CO2e]: ['kgCO2e', 'GgCO2e'],
  year: ['year'],
} satisfies AutoConversionUnitsRecord;

const autoConversionUnits = {
  [UnitSystem.Metric]: metricDefaultConversionUnits,
};

type AcceptableUnit = MetricUnit | CurrencyUnit | CO2eUnit;
export const getAcceptableAutoOutputUnits = (
  unit: string,
  unitCategory: UnitCategory | undefined,
  unitSystem: UnitSystem.Metric = UnitSystem.Metric, // only metric is supported for now
):
  | AcceptableUnit[]
  | BreakPointsInstance<AcceptableUnit, AcceptableUnit>
  | undefined => {
  const defaults = autoConversionUnits[unitSystem];
  const mathJsUnit = mathJS.unit(unit);

  const unitDefaults = defaults[unit];
  const derivedSiUnitDefaults = tryCatch(() => {
    // this fails for units that cannot be converted to SI. But it's just a safeguard, so no worries.
    const siUnit = mathJsUnit.toSI().formatUnits().replaceAll(' ', '');
    return defaults[siUnit];
  }, null).value;

  const unitCategoryDefaults = unitCategory && defaults[unitCategory];

  return unitDefaults ?? derivedSiUnitDefaults ?? unitCategoryDefaults;
};
